<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import {
  VesselClassPayload,
  VesselClassResponse,
} from "@/types/interface/vessel-class";
import { useVesselClass } from "@/stores/master/vessel-class";

const idVesselType = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const vesselClassStore = useVesselClass();
const detailVesselClass = ref<VesselClassResponse>();

const validationSchema = toTypedSchema(
  zod.object({
    name: zod.string().nonempty({ message: "Name is required" }),
    code: zod.string().nonempty({ message: "Code is required" }),
    year: zod.string().nonempty({ message: "Year is required" }),
    head_office: zod.string().nonempty({ message: "Head Office is required" }),
    iacs_member: zod.boolean(),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: name } = useField<string>("name");
const { value: code } = useField<string>("code");
const { value: year } = useField<string>("year");
const { value: head_office } = useField<string>("head_office");
const { value: iacs_member } = useField<boolean>("iacs_member");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;
  const payloadVesselType: VesselClassPayload = {
    name: name.value,
    code: code.value,
    year: year.value,
    head_office: head_office.value,
    iacs_member: true,
  };

  const responseApi = ref<any>();

  if (isEdit.value == true) {
    responseApi.value = await vesselClassStore.dispatchUpdate(
      idVesselType.value,
      payloadVesselType
    );
  } else {
    responseApi.value = await vesselClassStore.dispatchCreate(
      payloadVesselType
    );
  }

  const { success, message } = responseApi.value;
  if (success == true) {
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value
          ? "Vessel Class Successfully Updated"
          : "Vessel Class Successfully Added"
      );
      router.push("/master/vessel-class");
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadDetailVesselClass = async () => {
  const { success, message } = await vesselClassStore.dispatchDetail(
    idVesselType.value
  );
  if (success) {
    detailVesselClass.value = await vesselClassStore?.vesselClass?.data;
    name.value = detailVesselClass.value?.name;
    code.value = detailVesselClass.value?.code;
    year.value = detailVesselClass.value?.year;
    head_office.value = detailVesselClass.value?.head_office;
    iacs_member.value =
      detailVesselClass.value?.iacs_member == 1 ? true : false;
  }
};

onMounted(() => {
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idVesselType.value = router.currentRoute.value.params.id.toString();
    loadDetailVesselClass();
  }
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create Vessel Class</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/master/vessel-type"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List Vessel Class
            </a>
          </div>
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading"
          >
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="name" class="form-label label text-secondary"
                    >Name <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="name"
                      v-model="name"
                      placeholder="Name"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.name }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="code" class="form-label label text-secondary"
                    >Code <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="code"
                      v-model="code"
                      placeholder="Vessel Type Code"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.code }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="year" class="form-label label text-secondary"
                    >Year <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="year"
                      v-model="year"
                      placeholder="Vessel Year"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.year }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="head_office"
                    class="form-label label text-secondary"
                    >Head Office <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="head_office"
                      v-model="head_office"
                      placeholder="Vessel Head Office"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.head_office }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="iacs_member"
                    class="form-label label text-secondary"
                  >
                    IACS Member <span class="text-danger">*</span>
                  </label>
                  <div class="d-flex flex-wrap gap-3 mb-4">
                    <div class="d-flex align-items-center form-check style">
                      <div class="flex-shrink-0">
                        <input
                          class="form-check-input"
                          type="radio"
                          style="width: 20px; height: 20px"
                          name="iacs_member"
                          v-model="iacs_member"
                          :value="true"
                          id="iacs_member1"
                        />
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <label
                          class="form-check-label ps-0 fs-14 fw-medium text-secondary"
                          for="iacs_member1"
                        >
                          Yes
                        </label>
                      </div>
                    </div>

                    <div class="d-flex align-items-center form-check style">
                      <div class="flex-shrink-0">
                        <input
                          class="form-check-input"
                          type="radio"
                          style="width: 20px; height: 20px"
                          name="iacs_member"
                          v-model="iacs_member"
                          :value="false"
                          id="iacs_member2"
                        />
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <label
                          class="form-check-label ps-0 fs-14 fw-medium text-secondary"
                          for="iacs_member2"
                        >
                          No
                        </label>
                      </div>
                    </div>
                  </div>
                  <span class="text-danger">{{ errors.iacs_member }}</span>
                </div>
              </div>

              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
